import React, {
  memo, useCallback, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import {
  Box, TableCell, TableHead, TableRow,
} from '@mui/material';
import { getSortDirection } from './utils';

function DocTableListerHeader({
  columns,
  onSetOrder,
  order,
  onSetColumnSizes,
  columnSizes,
}) {
  const ref = useRef();
  const node = ref.current;
  const sizes = useRef({});
  const onResize = useCallback(
    (clsEntrys) => {
      if (node) {
        const { width } = node.getBoundingClientRect();
        if (width) {
          sizes.current = clsEntrys.reduce(
            (R, { contentRect, target }) => ({
              ...R,
              [target.dataset.col]: `${(contentRect.width * 100) / width}%`,
            }),
            {},
          );
          onSetColumnSizes(sizes.current);
        }
      }
    },
    [node, onSetColumnSizes],
  );
  const observer = useMemo((() => new ResizeObserver(onResize)), [onResize]);
  useEffect(
    () => {
      if (columns && node) {
        sizes.current = columns.reduce((R, col) => ({ ...R, [col.key]: columnSizes[col.key] }), {});
        node.querySelectorAll('[data-col]').forEach(
          (subnode) => observer.observe(subnode),
        );
        return () => {
          observer.disconnect();
        };
      }
      return null;
    },
    [columnSizes, columns, node, observer, onSetColumnSizes],
  );
  return (
    <TableHead>

      <TableRow>
        {columns.map((dc, i) => (
          <TableCell
            key={dc.key}
            onClick={(e) => dc.orderable && onSetOrder(e, dc.key)}
            data-col={dc.key}
            style={{ width: i === (columns.length - 1) ? 'inherit' : columnSizes[dc.key] }}
            className={dc.orderable ? 'orderable' : ''}
            sortDirection={getSortDirection(dc.key, order)}
          >
            <Box
              position="absolute"
              sx={{
                top: 0, left: 0, right: 0, textAlign: 'center',
              }}
              color="grey.700"
            >
              {order.column === dc.key && order.isAscending && (
              <FontAwesomeIcon icon={faAngleUp} />
              )}
              {order.column === dc.key && !order.isAscending && (
              <FontAwesomeIcon icon={faAngleDown} />
              )}
            </Box>
            {dc.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

DocTableListerHeader.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.string,
    orderable: PropTypes.bool,
  })).isRequired,
  onSetOrder: PropTypes.func.isRequired,
  order: PropTypes.shape({
    column: PropTypes.string,
    isAscending: PropTypes.bool,
  }).isRequired,
  columnSizes: PropTypes.shape({}),
  onSetColumnSizes: PropTypes.func.isRequired,
};

DocTableListerHeader.defaultProps = {
  columnSizes: {},
};

export default memo(DocTableListerHeader);
